import { areObjectIdsEqual } from 'helpers/idComparison';
import { swSignals } from 'serviceWorker/events/swSignals';
import * as config from 'serviceWorker/db/config';
import { clearProjectData } from 'serviceWorker/services/dataClearer';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';
import type { ProjectModel } from 'shared/domain/project/types/model';
import {
  holdsServiceWorkerConfig,
  ServiceWorkerConfigInput,
} from 'shared/domain/serviceWorkerConfig/serviceWorkerConfigModel';

/**
 * Stores the page's config (keeping the project and organization the worker
 * already has when the page sends none) and signals it inside the worker.
 * Another project than the stored one replaces the project data, as a switch
 * does. Resolves once stored.
 */
export async function applyServiceWorkerConfig(input: ServiceWorkerConfigInput): Promise<void> {
  const stored = await config.get();
  const projectId = input.projectId ?? stored?.projectId ?? '';
  // The stored timezone is that project's: another project without one gets none.
  const storedTimezoneOfProject = areObjectIdsEqual(stored?.projectId, projectId) ? stored?.timezone : undefined;
  const next = {
    ...input,
    projectId,
    organizationId: input.organizationId ?? stored?.organizationId,
    timezone: input.timezone ?? storedTimezoneOfProject,
  };

  // The project changed without a switch (the worker stopped during one, the page restores another project).
  const replacesProject = Boolean(stored?.projectId) && !areObjectIdsEqual(stored?.projectId, projectId);
  if (replacesProject) {
    // As a switch: what runs for the previous project is aborted and outdated before the config changes.
    swSignals.projectDataReplaced.emit();
  }
  if (!stored || !holdsServiceWorkerConfig(stored, next)) {
    await config.add(next);
  }
  if (replacesProject) {
    // The config is stored either way: a failed flush leaves collections of another project, read as missing.
    await clearProjectData().catch((error: unknown) =>
      swLog('SW could not flush the data of the previous project', LogLevel.ERROR, error, { projectId })
    );
  }
  // Processes refresh their project cache (PT-6894).
  swSignals.configApplied.emit();
}

/**
 * Points the stored config at the project the user switches to, before the
 * switch starts any download: the syncs call the API for that project
 * without waiting for the page to send it. The page's config sends the same
 * values afterwards. Without a stored config the page has not configured the
 * worker yet, and its first config brings the project.
 */
export async function applySelectedProjectToConfig({ _id, organizationId, timezone }: ProjectModel): Promise<void> {
  const stored = await config.get();
  if (!stored) {
    return;
  }
  await config.add({
    locale: stored.locale,
    frontendVersion: stored.frontendVersion,
    api: stored.api,
    projectId: _id,
    organizationId,
    timezone,
  });
}
